import { useState } from "react";

import { ArrowUpRight, ChevronLeft, ExternalLink, FileText } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import useBrandRouteGuard from "../../use-brand-route-guard";
import useBrandPublisherInvoices from "./use-brand-publisher-invoices";
import InvoiceDetailModal from "@panel/shared/invoice/components/invoice-detail-modal";
import { KYC_LABEL, KYC_TONE, INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE } from "@src/utility/constants/panel/common";
import { formatCurrency } from "@src/utility/helpers/utils";
import { formatDate } from "@src/utility/helpers/date";

// Brand-side view of the ops publisher-invoices screen. The brand column is
// dropped — every row here belongs to the brand in the URL.
const BrandPublisherInvoices = () => {
    const { id } = useParams();
    const { state } = useLocation();
    const { brandId } = useBrandRouteGuard();

    const { invoices, loading, stats, pagination, handlePageChange, handlePerRowsChange } = useBrandPublisherInvoices(brandId, id);
    const publisher = state?.publisher;
    const publisherName = publisher?.holder_name || invoices[0]?.holder_name || "Publisher";

    const [detailTarget, setDetailTarget] = useState(null);

    const columns = [
        {
            Header  : "Invoice",
            accessor: "invoice_number",
            // The stored tax invoice is one click away from the row itself; the
            // action column's modal is for everything around it.
            Cell    : ({ value, row }) => (row.invoice_url ? (
                <a
                    href={row.invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.invoice_meta?.original_name || row.invoice_meta?.name || "Tax invoice"}
                    className="inline-flex items-center gap-1.5 font-medium text-gray-900 hover:text-primary hover:underline">
                    {value}
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
            ) : <span className="font-medium text-gray-900">{value}</span>)
        },
        { Header: "Period", accessor: "payout_period", Cell: ({ value }) => <span className="text-gray-700">{value ? formatDate(value) : "—"}</span> },
        {
            Header  : "Debit note",
            accessor: "debit_note_number",
            Cell    : ({ value }) => (value ? (
                <Link to={`/brand/${brandId}/debit-note`} className="font-medium text-accent hover:underline">
                    {value}
                </Link>
            ) : <span className="text-gray-700">—</span>)
        },
        { Header: "Status", accessor: "status", Cell: ({ value }) => <StatusChip tone={INVOICE_STATUS_TONE[value] || "neutral"} label={INVOICE_STATUS_LABEL[value] || value} /> },
        { Header: "Reference", accessor: "payment_reference", Cell: ({ value }) => <span className="text-gray-700">{value || "—"}</span> },
        { Header: "Amount", accessor: "amount", Cell: ({ value }) => <span className="font-medium text-gray-900">{formatCurrency(value)}</span> },
        {
            Header  : "Action",
            accessor: "_id",
            Cell    : ({ row }) => (
                <button
                    type="button"
                    onClick={() => setDetailTarget(row)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                    View invoice
                    <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
            )
        }
    ];

    return (
        <div className="p-6">
            <div className="flex items-center justify-between">
                <Link to={`/brand/${brandId}/publishers`} className="inline-flex items-center gap-1 text-sm text-muted hover:text-gray-700">
                    <ChevronLeft className="h-4 w-4" />
                    All publishers
                </Link>
                <div className="flex items-center gap-2">
                    {publisher?.kyc_status && <StatusChip tone={KYC_TONE[publisher.kyc_status] || "neutral"} label={KYC_LABEL[publisher.kyc_status] || publisher.kyc_status} />}
                </div>
            </div>

            <h1 className="mt-2 text-2xl font-semibold tracking-tight">{publisherName}</h1>
            <p className="mt-1 text-sm text-muted">{publisher?.email || invoices[0]?.email || "—"}</p>

            <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Invoices</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{stats.total_invoices}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Outstanding</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{formatCurrency(stats.outstanding)}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Paid to date</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{stats.paid_to_date ? formatCurrency(stats.paid_to_date) : "—"}</p>
                </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-4">
                    <FileText className="h-4 w-4 text-muted" />
                    <h2 className="text-sm font-semibold text-gray-900">Invoices ({pagination.total})</h2>
                </div>
                <Datatable
                    columns={columns}
                    data={invoices}
                    loading={loading}
                    pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    customNoData={<span className="text-sm text-muted">No invoices for this publisher yet.</span>} />
            </div>

            <InvoiceDetailModal
                open={Boolean(detailTarget)}
                onClose={() => setDetailTarget(null)}
                invoice={detailTarget} />
        </div>
    );
};

export default BrandPublisherInvoices;
